import React, { useEffect, useMemo, useRef, useState } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import Separator from "../ui/Separator";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Box,
  Camera,
  Check,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  useActuators,
  useBodies,
  useJoints,
  useTendons,
  useWorldBodies,
  useSceneActions,
} from "../../stores/sceneStore";
import { useFileStore, useFileActions } from "../../stores/fileStore";
import { analyzeQposStructure } from "../../core/keyframeHandler";
import { eulerDegreesToQuaternion, quaternionToEulerDegrees } from "../../core/transform";
import { getHistoryActions } from "../../stores/historyStore";
import { getSceneState } from "../../stores/sceneStore";
import { commitBodyTransform } from "../../viewer/utils/commitTransform";
import { buildBodyParentMap, buildBodyRootMap } from "../../utils/bodyHierarchy";
import { getBodyTree } from "../../stores/sceneActions/bodyUtils";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import OffsetEditor from "./OffsetEditor";
import { BodyVisibilityToolbar } from "../../selected-body-visibility";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import GripperAttachmentSection from "./GripperAttachmentSection";
import { usePanelStore } from "../../stores/panelStore";
import Tooltip from "../ui/Tooltip";
import Checkbox from "../ui/Checkbox";
import {
  INSPECTOR_CHROME_SECTION_ICON_CLASS,
  INSPECTOR_SECTION_TITLE_TO_BODY_PAD_CLASS,
} from "../../utils/inspectorChromeButtonClasses";
import PanelDeleteIconButton from "../draggable-panels/PanelDeleteIconButton";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import { addDefaultJoint } from "../../utils/bodyAddActions";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { hasPositionLikeActuatorForJoint } from "../../physics/jointLabelActuatorDrag";

const KEYFRAME_OWNER_SEPARATOR = " :: ";
const initializedNeutralPoseScopes = new Set();

const equalWorldBodyRootEntry = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.uuid === b.uuid && a.name === b.name;
};

const renameKeyframeKey = (keyframes, oldName, newName) => {
  const nextKeyframes = {};

  Object.entries(keyframes || {}).forEach(([name, value]) => {
    nextKeyframes[name === oldName ? newName : name] = value;
  });

  return nextKeyframes;
};

const markNeutralPoseDeleted = (fullName) => {
  if (!fullName) return;
  useFileStore.setState((state) => ({
    keyframeDetails: {
      ...(state.keyframeDetails || {}),
      [fullName]: {
        ...(state.keyframeDetails?.[fullName] || {}),
        autoNeutralDeleted: true,
      },
    },
  }));
};

const findFreeJointUuid = (joints, jointUuids) => {
  if (!joints || !Array.isArray(jointUuids)) return null;
  for (let index = 0; index < jointUuids.length; index += 1) {
    const jointUuid = jointUuids[index];
    if (joints?.[jointUuid]?.kind === "free") return jointUuid;
  }
  return null;
};

const isScalarJoint = (joint) => joint?.kind === "hinge" || joint?.kind === "slide";

const getJointsFromBodyTreeInOrder = (bodyTree, jointsMap) => {
  const ordered = [];
  const seen = new Set();

  const visit = (node) => {
    if (!node) return;
    (node.joints || []).forEach((jointUuid) => {
      const key = String(jointUuid);
      if (seen.has(key)) return;
      const joint = jointsMap?.[key];
      if (!joint) return;
      ordered.push(joint);
      seen.add(key);
    });
    (node.children || []).forEach((child) => visit(child));
  };

  (bodyTree || []).forEach((root) => visit(root));
  Object.values(jointsMap || {}).forEach((joint) => {
    const key = String(joint?.uuid || "");
    if (!key || seen.has(key)) return;
    ordered.push(joint);
    seen.add(key);
  });
  return ordered;
};

const getObjectScopedJointEntries = (bodies, joints, worldBodies, rootBodyUuid) => {
  if (!rootBodyUuid || !bodies?.[rootBodyUuid]) return [];
  const bodyTree = getBodyTree({ bodies, worldBodies });
  const orderedJoints = getJointsFromBodyTreeInOrder(bodyTree, joints);
  const rootMap = buildBodyRootMap(bodies || {}, worldBodies || []);
  const jointOwnerBodyMap = {};

  Object.values(bodies || {}).forEach((body) => {
    if (!body || !Array.isArray(body.joints)) return;
    body.joints.forEach((jointUuid) => {
      if (typeof jointUuid === "string") jointOwnerBodyMap[String(jointUuid)] = body.uuid;
    });
  });

  return orderedJoints
    .map((joint) => {
      const ownerBodyUuid = jointOwnerBodyMap[String(joint.uuid)];
      if (!ownerBodyUuid || rootMap[ownerBodyUuid] !== rootBodyUuid) return null;
      return { joint, hostBody: bodies?.[ownerBodyUuid] || null };
    })
    .filter(Boolean);
};

const numbersClose = (a, b, tolerance = 1e-5) =>
  Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= tolerance;

const quaternionsClose = (currentQuat, poseQuat) => {
  if (!Array.isArray(currentQuat) || !Array.isArray(poseQuat)) return false;
  const direct = [0, 1, 2, 3].every((offset) =>
    numbersClose(Number(currentQuat[offset]), Number(poseQuat[offset]))
  );
  const negated = [0, 1, 2, 3].every((offset) =>
    numbersClose(Number(currentQuat[offset]), -Number(poseQuat[offset]))
  );
  return direct || negated;
};

const clampScalarJointValue = (joint, value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  if (!isScalarJoint(joint)) return numericValue;
  const range = Array.isArray(joint.range) ? joint.range : null;
  if (!range) return numericValue;
  const shouldClampToRange = joint.kind !== "slide" || joint.useLimits !== "false";
  if (!shouldClampToRange) return numericValue;
  const min = Number(range[0]);
  const max = Number(range[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return numericValue;
  return Math.max(min, Math.min(max, numericValue));
};

const getNeutralQposForJointEntries = (jointEntries) => {
  if (!Array.isArray(jointEntries) || !jointEntries.some(({ joint }) => joint?.kind !== "free")) {
    return null;
  }
  return jointEntries.flatMap(({ joint, hostBody }) => {
    if (!joint) return [];
    if (joint.kind === "free") {
      const pos = Array.isArray(hostBody?.position) ? hostBody.position : [0, 0, 0];
      const quat = Array.isArray(hostBody?.orientation) ? hostBody.orientation : [1, 0, 0, 0];
      return [
        Number(pos[0]) || 0,
        Number(pos[1]) || 0,
        Number(pos[2]) || 0,
        Number(quat[0]) || 1,
        Number(quat[1]) || 0,
        Number(quat[2]) || 0,
        Number(quat[3]) || 0,
      ];
    }
    if (joint.kind === "ball") return [1, 0, 0, 0];
    if (isScalarJoint(joint)) return [0];
    return [];
  });
};

const qposValueGroupsClose = (jointEntries, expectedValues, actualValues) => {
  let ptr = 0;
  for (const { joint } of jointEntries) {
    if (!joint || joint.kind === "free") continue;
    if (joint.kind === "ball") {
      if (ptr + 3 >= expectedValues.length || ptr + 3 >= actualValues.length) return false;
      if (
        !quaternionsClose(
          expectedValues.slice(ptr, ptr + 4).map(Number),
          actualValues.slice(ptr, ptr + 4).map(Number)
        )
      ) {
        return false;
      }
      ptr += 4;
    } else if (isScalarJoint(joint)) {
      if (ptr >= expectedValues.length || ptr >= actualValues.length) return false;
      if (!numbersClose(Number(expectedValues[ptr]), Number(actualValues[ptr]))) return false;
      ptr += 1;
    }
  }
  return ptr === expectedValues.length && ptr === actualValues.length;
};

const qposEntryGroupsClose = (jointEntries, expectedValues, actualValues) => {
  let ptr = 0;
  for (const { joint } of jointEntries) {
    if (!joint) continue;
    if (joint.kind === "free") {
      if (ptr + 6 >= expectedValues.length || ptr + 6 >= actualValues.length) return false;
      const positionsClose = [0, 1, 2].every((offset) =>
        numbersClose(Number(expectedValues[ptr + offset]), Number(actualValues[ptr + offset]))
      );
      if (!positionsClose) return false;
      if (
        !quaternionsClose(
          expectedValues.slice(ptr + 3, ptr + 7).map(Number),
          actualValues.slice(ptr + 3, ptr + 7).map(Number)
        )
      ) {
        return false;
      }
      ptr += 7;
    } else if (joint.kind === "ball") {
      if (ptr + 3 >= expectedValues.length || ptr + 3 >= actualValues.length) return false;
      if (
        !quaternionsClose(
          expectedValues.slice(ptr, ptr + 4).map(Number),
          actualValues.slice(ptr, ptr + 4).map(Number)
        )
      ) {
        return false;
      }
      ptr += 4;
    } else if (isScalarJoint(joint)) {
      if (ptr >= expectedValues.length || ptr >= actualValues.length) return false;
      if (!numbersClose(Number(expectedValues[ptr]), Number(actualValues[ptr]))) return false;
      ptr += 1;
    }
  }
  return ptr === expectedValues.length && ptr === actualValues.length;
};

const qposMatchesNeutralPose = (jointEntries, poseQpos, neutralQpos) => {
  if (!Array.isArray(jointEntries) || !Array.isArray(poseQpos) || !Array.isArray(neutralQpos)) {
    return false;
  }
  const joints = jointEntries.map(({ joint }) => joint).filter(Boolean);
  if (joints.length === 0) return false;

  if (poseQpos.length === neutralQpos.length) {
    return qposEntryGroupsClose(jointEntries, neutralQpos, poseQpos);
  }

  const structure = analyzeQposStructure(poseQpos, joints);
  const jointValues = Array.isArray(structure?.jointValues) ? structure.jointValues : [];
  const mappedJoints = Array.isArray(structure?.mappingJoints) ? structure.mappingJoints : joints;
  const mappedEntries = mappedJoints
    .map((joint) => jointEntries.find((entry) => entry.joint === joint))
    .filter(Boolean);
  const neutralJointValues = mappedEntries.flatMap(({ joint }) => {
    if (!joint || joint.kind === "free") return [];
    if (joint.kind === "ball") return [1, 0, 0, 0];
    if (isScalarJoint(joint)) return [0];
    return [];
  });

  return qposValueGroupsClose(mappedEntries, neutralJointValues, jointValues);
};

const getPhysicsPoseApplyState = (jointEntries, poseQpos) => {
  if (!Array.isArray(jointEntries) || !Array.isArray(poseQpos)) {
    return { canApply: false, reason: "Pose data is unavailable" };
  }

  const joints = jointEntries.map(({ joint }) => joint).filter(Boolean);
  if (joints.length === 0) return { canApply: false, reason: "Pose has no joints to drive" };

  const freeJoints = joints.filter((joint) => joint.kind === "free");
  const scalarJoints = joints.filter(isScalarJoint);
  const freeOnlyObjectKeyframe =
    freeJoints.length === 1 &&
    scalarJoints.length === 0 &&
    joints.length === 1 &&
    (poseQpos.length === 7 || poseQpos.length === 8);
  if (freeOnlyObjectKeyframe) {
    return {
      canApply: false,
      reason: "Free-body poses cannot be applied while physics is running",
    };
  }

  const scalarTargets = [];
  const freePlusScalarsKeyframe =
    freeJoints.length === 1 &&
    scalarJoints.length > 0 &&
    poseQpos.length === 7 + scalarJoints.length;
  if (freePlusScalarsKeyframe) {
    scalarTargets.push(...scalarJoints);
  } else {
    const structure = analyzeQposStructure(poseQpos, joints);
    const jointValues = Array.isArray(structure?.jointValues) ? structure.jointValues : [];
    if (structure?.hasBallJoints) {
      let ptr = 0;
      for (const joint of joints) {
        if (!joint || joint.kind === "free") continue;
        if (joint.kind === "ball") {
          if (ptr + 3 < jointValues.length) {
            return {
              canApply: false,
              reason: "Ball-joint poses cannot be applied while physics is running",
            };
          }
          ptr += 4;
        } else if (isScalarJoint(joint)) {
          if (ptr < jointValues.length) scalarTargets.push(joint);
          ptr += 1;
        }
      }
    } else {
      const mappedJoints = Array.isArray(structure?.mappingJoints)
        ? structure.mappingJoints
        : joints;
      mappedJoints.forEach((joint, index) => {
        if (index < jointValues.length && isScalarJoint(joint)) scalarTargets.push(joint);
      });
    }
  }

  const uniqueScalarTargets = [
    ...new Map(scalarTargets.map((joint) => [joint.uuid, joint])).values(),
  ];
  if (uniqueScalarTargets.length === 0) {
    return { canApply: false, reason: "Pose has no position-actuated joints to drive" };
  }

  const drivenTargets = uniqueScalarTargets.filter((joint) =>
    hasPositionLikeActuatorForJoint(joint.uuid)
  );
  if (drivenTargets.length === 0) {
    return {
      canApply: false,
      reason: "Pose has no joints with position actuator targets",
    };
  }

  return { canApply: true, reason: "Apply pose through position actuator targets" };
};

const qposMatchesCurrentAppliedPose = (jointEntries, poseQpos) => {
  if (!Array.isArray(jointEntries) || !Array.isArray(poseQpos)) return false;
  const joints = jointEntries.map(({ joint }) => joint).filter(Boolean);
  if (joints.length === 0) return false;

  const freeJoints = joints.filter((joint) => joint.kind === "free");
  const scalarJoints = joints.filter(isScalarJoint);
  const freeOnlyObjectKeyframe =
    freeJoints.length === 1 &&
    scalarJoints.length === 0 &&
    joints.length === 1 &&
    (poseQpos.length === 7 || poseQpos.length === 8);

  if (freeOnlyObjectKeyframe) {
    const freeEntry = jointEntries.find(({ joint }) => joint?.kind === "free");
    const body = freeEntry?.hostBody;
    const pos = Array.isArray(body?.position) ? body.position : [0, 0, 0];
    const quat = Array.isArray(body?.orientation) ? body.orientation : [1, 0, 0, 0];
    return (
      [0, 1, 2].every((index) => numbersClose(Number(pos[index]), Number(poseQpos[index]))) &&
      quaternionsClose(quat, poseQpos.slice(3, 7).map(Number))
    );
  }

  const structure = analyzeQposStructure(poseQpos, joints);
  const jointValues = Array.isArray(structure?.jointValues) ? structure.jointValues : [];
  const hasBallJoints = joints.some((joint) => joint?.kind === "ball");

  if (hasBallJoints) {
    let ptr = 0;
    for (const { joint } of jointEntries) {
      if (!joint || joint.kind === "free") continue;
      if (joint.kind === "ball") {
        if (ptr + 3 >= jointValues.length) return false;
        const currentQuat = Array.isArray(joint.quat) ? joint.quat : [1, 0, 0, 0];
        if (!quaternionsClose(currentQuat, jointValues.slice(ptr, ptr + 4).map(Number))) {
          return false;
        }
        ptr += 4;
      } else if (isScalarJoint(joint)) {
        if (ptr >= jointValues.length) return false;
        const expected = clampScalarJointValue(joint, jointValues[ptr]);
        if (!numbersClose(Number(joint.ref) || 0, expected)) return false;
        ptr += 1;
      }
    }
    return true;
  }

  const mappedJoints = Array.isArray(structure?.mappingJoints) ? structure.mappingJoints : joints;
  let valueIndex = 0;
  for (const joint of mappedJoints) {
    if (!isScalarJoint(joint)) continue;
    if (valueIndex >= jointValues.length) return false;
    const expected = clampScalarJointValue(joint, jointValues[valueIndex]);
    if (!numbersClose(Number(joint.ref) || 0, expected)) return false;
    valueIndex += 1;
  }

  return valueIndex > 0 || poseQpos.length === 0;
};

const ObjectMovementSection = React.memo(function ObjectMovementSection({
  objectRootUuid,
  objectRootBody,
  objectRootParentUuid,
  joints,
  physicsAuthoringLocked,
  sceneActions,
}) {
  const freeJointUuid = findFreeJointUuid(joints, objectRootBody?.joints);
  const movesFreely = !!freeJointUuid;
  const hasNonFreeJoint = Array.isArray(objectRootBody?.joints)
    ? objectRootBody.joints.some((jointUuid) => joints?.[jointUuid]?.kind !== "free")
    : false;
  const canAddFreeMovement = !objectRootParentUuid && !hasNonFreeJoint;
  const disabled = physicsAuthoringLocked || (!movesFreely && !canAddFreeMovement);

  const disabledReason = physicsAuthoringLocked
    ? "Stop physics to change how this object moves"
    : objectRootParentUuid
      ? "Only top-level objects can move freely through space"
      : hasNonFreeJoint
        ? "Remove this object's other joints before making it move freely through space"
        : null;
  const fixedInSpaceTooltip =
    disabledReason ||
    "This object moves freely during physics simulation unless it is fixed in space.";

  const handleFixedInSpaceChange = (event) => {
    if (physicsAuthoringLocked || !objectRootUuid) return;
    const nextFixedInSpace = event.target.checked;
    const currentState = getSceneState();
    const currentRootBody = currentState?.bodies?.[objectRootUuid] || null;
    const currentRootJointRefs = Array.isArray(currentRootBody?.joints)
      ? currentRootBody.joints
      : [];
    const currentFreeJointUuids = currentRootJointRefs.filter(
      (jointUuid) => currentState?.joints?.[jointUuid]?.kind === "free"
    );
    if (!nextFixedInSpace) {
      if (currentFreeJointUuids.length > 0 || !canAddFreeMovement) return;
      addDefaultJoint(sceneActions, objectRootUuid, "free");
      return;
    }
    currentFreeJointUuids.forEach((jointUuid) => sceneActions.removeJoint(jointUuid));
  };

  return (
    <InspectorPhysicsLockedCollapsibleWrap locked={physicsAuthoringLocked}>
      <Collapsible defaultOpen>
        <CollapsibleTrigger quiet>Movement in simulation</CollapsibleTrigger>
        <CollapsibleContent>
          <div className={INSPECTOR_SECTION_TITLE_TO_BODY_PAD_CLASS}>
            <Checkbox
              label="Fix in space"
              checked={!movesFreely}
              disabled={disabled}
              onChange={handleFixedInSpaceChange}
              aria-label="Fix object in space"
              tooltipContent={fixedInSpaceTooltip}
              className="rounded-md border border-gray-700 bg-gray-900/30 text-xs text-gray-200 hover:bg-gray-800/50"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </InspectorPhysicsLockedCollapsibleWrap>
  );
});

const ObjectPropertiesPanel = ({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
}) => {
  const selectedBodyUuid = useSelectionStore((s) => s.selectedBody);
  const selectedKeyframe = useSelectionStore((s) => s.selectedKeyframe);
  const lastSelectionSource = useSelectionStore((s) => s.lastSelectionSource);
  const sceneActions = useSceneActions();
  const joints = useJoints();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const _actuatorRuntimeWire = useActuators((actuators) =>
    Object.values(actuators || {})
      .map((actuator) =>
        [
          actuator?.uuid,
          actuator?.kind,
          actuator?.joint,
          actuator?.tendon,
          actuator?.tendonUuid,
        ].join(":")
      )
      .sort()
      .join("|")
  );
  const _tendonRuntimeWire = useTendons((tendons) =>
    Object.values(tendons || {})
      .map((tendon) =>
        [
          tendon?.uuid,
          tendon?.name,
          ...(Array.isArray(tendon?.joints) ? tendon.joints.map((entry) => entry?.joint) : []),
        ].join(":")
      )
      .sort()
      .join("|")
  );
  const keyframes = useFileStore((state) => state.keyframes);
  const keyframeDetails = useFileStore((state) => state.keyframeDetails);
  const _setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);
  const fileActions = useFileActions();
  const selectionActions = useSelectionStore.getState();
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const isMobile = useIsMobile();
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();

  const [localPosition, setLocalPosition] = useState([0, 0, 0]);
  const [localRotation, setLocalRotation] = useState([0, 0, 0]);
  const [localQuaternion, setLocalQuaternion] = useState([1, 0, 0, 0]);
  const [renamingKeyframeName, setRenamingKeyframeName] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [movementHighlighting, setMovementHighlighting] = useState(false);
  const movementSectionRef = useRef(null);

  /** Stable while parent links on the path from the selection to the root are unchanged. */
  const bodyAncestryKey = useBodies((allBodies) => {
    if (!selectedBodyUuid) return "";
    const parentMap = buildBodyParentMap(allBodies || {});
    const chain = [];
    let current = selectedBodyUuid;
    const visited = new Set();
    while (current && !visited.has(current)) {
      visited.add(current);
      chain.push(current);
      const parent = parentMap[current];
      if (!parent) break;
      current = parent;
    }
    return chain.join("\x1e");
  }, Object.is);

  /** Top ancestor in the body tree (world root for this object), ignoring object-properties link semantics. */
  const hierarchyRootUuid = useMemo(() => {
    if (!bodyAncestryKey) return null;
    const ids = bodyAncestryKey.split("\x1e").filter(Boolean);
    return ids.length ? ids[ids.length - 1] : null;
  }, [bodyAncestryKey]);

  const selectedObjectRootUuid = useMemo(() => {
    if (!selectedBodyUuid) return null;
    if (lastSelectionSource === "objectPropertiesLink") return selectedBodyUuid;
    return hierarchyRootUuid;
  }, [selectedBodyUuid, lastSelectionSource, hierarchyRootUuid]);

  /** World root for this selection — use for poses/keyframes so arm/gripper links do not scope to a child subtree. */
  const keyframeScopeWorldBodyUuid = hierarchyRootUuid || selectedObjectRootUuid;

  const worldBodyEntryForHierarchyRoot = useWorldBodies((wb) => {
    if (!hierarchyRootUuid) return null;
    const found = (wb || []).find((x) => {
      const uuid = typeof x === "string" ? x : x?.uuid;
      return uuid === hierarchyRootUuid;
    });
    if (found && typeof found === "object") {
      const uuid = typeof found.uuid === "string" ? found.uuid : hierarchyRootUuid;
      const name = typeof found.name === "string" ? found.name : null;
      return { uuid, name };
    }
    if (typeof found === "string") {
      return { uuid: found, name: null };
    }
    return null;
  }, equalWorldBodyRootEntry);

  const hierarchyRootBodyName = useBodies(
    (allBodies) => (hierarchyRootUuid ? allBodies?.[hierarchyRootUuid]?.name || "" : ""),
    Object.is
  );

  const keyframeOwnerLabel = useMemo(() => {
    if (!hierarchyRootUuid) return null;
    const fromWorldBody = worldBodyEntryForHierarchyRoot?.name ?? null;
    const fromMjcf = hierarchyRootBodyName || null;
    return fromWorldBody || fromMjcf || null;
  }, [hierarchyRootUuid, worldBodyEntryForHierarchyRoot, hierarchyRootBodyName]);

  const hierarchyRootLabel = useMemo(() => {
    if (!hierarchyRootUuid) return null;
    // Prefer the MJCF/scene body name (e.g. "base"); worldBodies[].name is the object label (asset/instance).
    const fromBody = hierarchyRootBodyName || null;
    const fromWorldBody = worldBodyEntryForHierarchyRoot?.name ?? null;
    const raw = fromBody || fromWorldBody || hierarchyRootUuid;
    return stripPrefix(raw);
  }, [hierarchyRootUuid, hierarchyRootBodyName, worldBodyEntryForHierarchyRoot]);

  const keyframePrefix = keyframeOwnerLabel
    ? `${keyframeOwnerLabel}${KEYFRAME_OWNER_SEPARATOR}`
    : null;

  const scopedKeyframeNamesWire = useFileStore((state) => {
    if (!keyframePrefix) return "";
    return Object.keys(state.keyframes || {})
      .filter((name) => name.startsWith(keyframePrefix))
      .sort()
      .join("\x1f");
  }, Object.is);

  const scopedKeyframes = useMemo(() => {
    if (!keyframePrefix) return [];
    if (!scopedKeyframeNamesWire) return [];
    return scopedKeyframeNamesWire
      .split("\x1f")
      .filter(Boolean)
      .map((fullName) => ({
        fullName,
        poseName: fullName.slice(keyframePrefix.length) || fullName,
      }));
  }, [keyframePrefix, scopedKeyframeNamesWire]);

  const currentObjectJointEntries = useMemo(
    () => getObjectScopedJointEntries(bodies, joints, worldBodies, keyframeScopeWorldBodyUuid),
    [bodies, joints, worldBodies, keyframeScopeWorldBodyUuid]
  );

  const activePoseFullName = useMemo(() => {
    if (currentObjectJointEntries.length === 0 || scopedKeyframes.length === 0) {
      return null;
    }
    if (
      authoringLocked &&
      selectedKeyframe &&
      scopedKeyframes.some((entry) => entry.fullName === selectedKeyframe)
    ) {
      return selectedKeyframe;
    }
    const match = scopedKeyframes.find((entry) =>
      qposMatchesCurrentAppliedPose(currentObjectJointEntries, keyframes[entry.fullName])
    );
    return match?.fullName || null;
  }, [authoringLocked, currentObjectJointEntries, keyframes, scopedKeyframes, selectedKeyframe]);

  const runtimePoseApplyStateByFullName = useMemo(() => {
    if (
      !authoringLocked ||
      currentObjectJointEntries.length === 0 ||
      scopedKeyframes.length === 0
    ) {
      return new Map();
    }
    return new Map(
      scopedKeyframes.map((entry) => [
        entry.fullName,
        getPhysicsPoseApplyState(currentObjectJointEntries, keyframes[entry.fullName]),
      ])
    );
  }, [authoringLocked, currentObjectJointEntries, keyframes, scopedKeyframes]);

  useEffect(() => {
    if (!keyframePrefix || !keyframeScopeWorldBodyUuid) return;
    const neutralScopeKey = `${keyframeScopeWorldBodyUuid}\x1f${keyframePrefix}`;
    if (initializedNeutralPoseScopes.has(neutralScopeKey)) return;
    const fullName = `${keyframePrefix}neutral`;
    if (keyframeDetails?.[fullName]?.autoNeutralDeleted === true) {
      initializedNeutralPoseScopes.add(neutralScopeKey);
      return;
    }
    const hasNeutral = scopedKeyframes.some((entry) => entry.poseName === "neutral");
    if (hasNeutral) {
      initializedNeutralPoseScopes.add(neutralScopeKey);
      return;
    }
    const neutralQpos = getNeutralQposForJointEntries(currentObjectJointEntries);
    if (!neutralQpos) return;
    const hasEquivalentNeutralPose = scopedKeyframes.some((entry) =>
      qposMatchesNeutralPose(currentObjectJointEntries, keyframes[entry.fullName], neutralQpos)
    );
    if (hasEquivalentNeutralPose) {
      initializedNeutralPoseScopes.add(neutralScopeKey);
      return;
    }
    useFileStore.setState((state) => {
      if (state.keyframes?.[fullName]) return {};
      return {
        keyframes: {
          ...(state.keyframes || {}),
          [fullName]: neutralQpos,
        },
      };
    });
    initializedNeutralPoseScopes.add(neutralScopeKey);
  }, [
    currentObjectJointEntries,
    keyframeDetails,
    keyframes,
    keyframePrefix,
    keyframeScopeWorldBodyUuid,
    scopedKeyframes,
  ]);

  const objectRootPoseFingerprint = useBodies((allBodies) => {
    if (!selectedObjectRootUuid) return "";
    const b = allBodies?.[selectedObjectRootUuid];
    if (!b) return "";
    const pos = b.position;
    const ori = b.orientation;
    if (!Array.isArray(pos) || !Array.isArray(ori)) return "";
    return `${pos.join(",")}|${ori.join(",")}`;
  }, Object.is);

  const objectRootBody = useBodies(
    (allBodies) => (selectedObjectRootUuid ? allBodies?.[selectedObjectRootUuid] || null : null),
    Object.is
  );

  const objectRootParentUuid = useBodies((allBodies) => {
    if (!selectedObjectRootUuid) return null;
    const parentMap = buildBodyParentMap(allBodies || {});
    return parentMap[selectedObjectRootUuid] || null;
  }, Object.is);

  useEffect(() => {
    if (!selectedObjectRootUuid) return;
    const bodyPos = sceneActions.getBodyPosition(selectedObjectRootUuid) || [0, 0, 0];
    const bodyQuat = sceneActions.getBodyRotation(selectedObjectRootUuid) || [1, 0, 0, 0];
    setLocalPosition(Array.isArray(bodyPos) ? [...bodyPos] : [0, 0, 0]);
    setLocalQuaternion(Array.isArray(bodyQuat) ? [...bodyQuat] : [1, 0, 0, 0]);
    setLocalRotation(quaternionToEulerDegrees(bodyQuat));
  }, [selectedObjectRootUuid, sceneActions, objectRootPoseFingerprint]);

  useEffect(() => {
    const handleAttention = (event) => {
      const bodyUuid = event?.detail?.bodyUuid ?? null;
      if (bodyUuid && bodyUuid !== selectedObjectRootUuid) return;
      window.setTimeout(() => {
        movementSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 80);
      setMovementHighlighting(true);
      requestInspectorChromeFlash();
    };
    window.addEventListener("scene-graph-object-movement-attention", handleAttention);
    return () =>
      window.removeEventListener("scene-graph-object-movement-attention", handleAttention);
  }, [selectedObjectRootUuid]);

  useEffect(() => {
    if (!movementHighlighting) return;
    const id = window.setTimeout(() => setMovementHighlighting(false), 1400);
    return () => window.clearTimeout(id);
  }, [movementHighlighting]);

  if (!selectedObjectRootUuid) {
    return null;
  }

  const handlePositionChange = (axis, value) => {
    if (!selectedObjectRootUuid) return;
    const currentPos = sceneActions.getBodyPosition(selectedObjectRootUuid) || [0, 0, 0];
    const currentQuat = sceneActions.getBodyRotation(selectedObjectRootUuid) || [1, 0, 0, 0];
    const nextPos = [...currentPos];
    nextPos[axis] = value;
    commitBodyTransform(sceneActions, selectedObjectRootUuid, nextPos, currentQuat);
  };

  const handleRotationChange = (axis, value) => {
    if (!selectedObjectRootUuid) return;
    const currentPos = sceneActions.getBodyPosition(selectedObjectRootUuid) || [0, 0, 0];
    const currentRot = quaternionToEulerDegrees(
      sceneActions.getBodyRotation(selectedObjectRootUuid)
    );
    currentRot[axis] = value;
    const nextQuat = eulerDegreesToQuaternion(currentRot);
    commitBodyTransform(sceneActions, selectedObjectRootUuid, currentPos, nextQuat);
  };

  const handleQuaternionChange = (idx, value) => {
    if (!selectedObjectRootUuid) return;
    const currentPos = sceneActions.getBodyPosition(selectedObjectRootUuid) || [0, 0, 0];
    const currentQuat = sceneActions.getBodyRotation(selectedObjectRootUuid) || [1, 0, 0, 0];
    const nextQuat = [...currentQuat];
    nextQuat[idx] = value;
    commitBodyTransform(sceneActions, selectedObjectRootUuid, currentPos, nextQuat);
  };

  const handleCreatePose = () => {
    if (!selectedObjectRootUuid || activePoseFullName) return;
    const existingNames = new Set(scopedKeyframes.map((entry) => entry.poseName));
    let counter = 1;
    let newName = `new keyframe ${counter}`;
    while (existingNames.has(newName)) {
      counter += 1;
      newName = `new keyframe ${counter}`;
    }
    getHistoryActions().save(getSceneState());
    fileActions.createKeyframe(newName, keyframeScopeWorldBodyUuid);
  };

  const getUniquePoseName = (baseName, excludedFullName = null) => {
    const normalizedBaseName = String(baseName || "").trim() || "new keyframe";
    const existingNames = new Set(
      scopedKeyframes
        .filter((entry) => entry.fullName !== excludedFullName)
        .map((entry) => entry.poseName)
    );
    let candidate = normalizedBaseName;
    let suffix = 2;
    while (existingNames.has(candidate)) {
      candidate = `${normalizedBaseName} (${suffix})`;
      suffix += 1;
    }
    return candidate;
  };

  const startRenamePose = (entry) => {
    if (authoringLocked) return;
    setRenamingKeyframeName(entry.fullName);
    setRenameDraft(entry.poseName);
  };

  const cancelRenamePose = () => {
    setRenamingKeyframeName(null);
    setRenameDraft("");
  };

  const commitRenamePose = (entry) => {
    if (authoringLocked) {
      cancelRenamePose();
      return;
    }
    if (!keyframePrefix) return;
    const trimmedName = renameDraft.trim();
    if (!trimmedName) {
      window.alert("Pose name cannot be empty.");
      return;
    }
    if (trimmedName.includes(KEYFRAME_OWNER_SEPARATOR)) {
      window.alert(`Pose name cannot include "${KEYFRAME_OWNER_SEPARATOR}".`);
      return;
    }

    const nextPoseName = getUniquePoseName(trimmedName, entry.fullName);
    const nextFullName = `${keyframePrefix}${nextPoseName}`;
    if (nextFullName === entry.fullName) {
      cancelRenamePose();
      return;
    }

    getHistoryActions().save(getSceneState());
    useFileStore.setState((state) => ({
      keyframes: renameKeyframeKey(state.keyframes, entry.fullName, nextFullName),
      keyframeDetails: {
        ...renameKeyframeKey(state.keyframeDetails || {}, entry.fullName, nextFullName),
        ...(entry.poseName === "neutral"
          ? {
              [entry.fullName]: {
                ...(state.keyframeDetails?.[entry.fullName] || {}),
                autoNeutralDeleted: true,
              },
            }
          : {}),
      },
    }));
    if (selectedKeyframe === entry.fullName) {
      selectionActions.selectKeyframe(nextFullName);
    }
    cancelRenamePose();
  };

  const handleOpenRootBodyProperties = () => {
    if (!hierarchyRootUuid) return;
    selectionActions.setLastSelectionSource("sceneTree");
    if (hierarchyRootUuid !== selectedBodyUuid) {
      selectionActions.selectBody(hierarchyRootUuid);
    }
    requestInspectorChromeFlash();
  };

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";
  const backLabel = canGoBack ? `Back (ALT + ←): ${quickNavBackTarget.label}` : "Back (ALT + ←)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + →): ${quickNavForwardTarget.label}`
    : "Forward (ALT + →)";
  const inspectRootBodyLabel = hierarchyRootLabel
    ? `Inspect Root Body: ${hierarchyRootLabel}`
    : "Inspect Root Body";
  const addPoseDisabled = authoringLocked || !!activePoseFullName;
  const capturePoseTooltip = authoringLocked
    ? "Stop physics to capture poses"
    : addPoseDisabled
      ? "Current pose is already saved"
      : "Capture current pose";
  const _quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    {
      key: "rootBody",
      label: inspectRootBodyLabel,
      icon: (
        <span className="relative block w-4 h-4" aria-hidden="true">
          <Box className="absolute inset-0 w-4 h-4" />
          <ArrowDown className="absolute -right-1 -bottom-1 w-2.5 h-2.5" />
        </span>
      ),
      disabled: !hierarchyRootUuid,
      onDefaultClick: handleOpenRootBodyProperties,
    },
  ].filter(Boolean);

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <BodyVisibilityToolbar
          bodyUuid={selectedObjectRootUuid}
          sectionTitle="Object Visibility"
          includeGraspOverlay
        />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <BodyVisibilityToolbar
        bodyUuid={selectedObjectRootUuid}
        sectionTitle="Object Visibility"
        includeGraspOverlay
      />

      <Separator />

      <GripperAttachmentSection
        worldBodyMapKey={hierarchyRootUuid}
        physicsLocked={authoringLocked}
      />

      <Separator />

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger
            quiet
            aria-label="Toggle Transform"
            actions={
              <TransformGizmoHeaderActions
                disabled={transformEditorBlock.disabled}
                disabledTitle={transformEditorBlock.title}
                showPositionGizmo={showPositionGizmo}
                showRotationGizmo={showRotationGizmo}
                onTogglePositionGizmo={togglePositionGizmo}
                onToggleRotationGizmo={toggleRotationGizmo}
              />
            }
          >
            <span className="min-w-0 truncate text-left">Transform</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-3">
              <OffsetEditor
                embedded
                globalPosition={localPosition}
                localPosition={localPosition}
                localRotationDeg={localRotation}
                quaternion={localQuaternion}
                onGlobalPositionChange={handlePositionChange}
                onPositionChange={handlePositionChange}
                onRotationChange={handleRotationChange}
                onQuaternionChange={handleQuaternionChange}
                disabled={transformEditorBlock.disabled}
                disabledTitle={transformEditorBlock.title}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>

      <Separator />

      <div
        ref={movementSectionRef}
        className={`rounded-md transition-colors duration-200 ${
          movementHighlighting ? "bg-sky-400/15" : ""
        }`}
      >
        <ObjectMovementSection
          objectRootUuid={selectedObjectRootUuid}
          objectRootBody={objectRootBody}
          objectRootParentUuid={objectRootParentUuid}
          joints={joints}
          physicsAuthoringLocked={authoringLocked}
          sceneActions={sceneActions}
        />
      </div>

      <Separator />

      <Collapsible defaultOpen>
        <CollapsibleTrigger
          quiet
          aria-label="Toggle Poses"
          actions={
            <>
              <Tooltip content={capturePoseTooltip}>
                <button
                  type="button"
                  aria-label="Capture current pose"
                  disabled={addPoseDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (addPoseDisabled) return;
                    handleCreatePose();
                  }}
                  className={`${INSPECTOR_CHROME_SECTION_ICON_CLASS} disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent disabled:hover:text-current`}
                >
                  <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </Tooltip>
            </>
          }
          actionsClassName="gap-1"
        >
          <span className="min-w-0 truncate text-left">Poses</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3">
            {scopedKeyframes.length === 0 ? (
              <div className="rounded border border-dashed border-gray-600/45 bg-gray-900/25 px-2 py-2">
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    if (addPoseDisabled) return;
                    handleCreatePose();
                  }}
                  disabled={addPoseDisabled}
                  className="inline-flex w-full items-center justify-center gap-1 text-xs text-gray-200 transition-colors hover:text-gray-100 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:text-gray-200"
                >
                  <span>Add new</span>
                  <Plus className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                </button>
              </div>
            ) : null}
            {scopedKeyframes.length > 0 ? (
              <div className="space-y-1">
                {scopedKeyframes.map((entry) => {
                  const isCurrentPose = activePoseFullName === entry.fullName;
                  const runtimeApplyState = runtimePoseApplyStateByFullName.get(entry.fullName);
                  const poseApplyDisabled = authoringLocked && !runtimeApplyState?.canApply;
                  const poseApplyTooltip = authoringLocked
                    ? runtimeApplyState?.reason || "Stop physics to apply this pose"
                    : null;
                  return (
                    <div
                      key={entry.fullName}
                      className={`menu-item group flex w-full items-center gap-2 rounded border px-3 py-2 transition-colors duration-150 ${
                        isCurrentPose
                          ? "border-gray-600 bg-[rgba(80,80,80,0.65)] text-gray-100"
                          : "border-gray-700 bg-gray-800/60 text-gray-200 hover:bg-gray-700/60"
                      }`}
                    >
                      <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                        {isCurrentPose ? (
                          <Check className="h-3 w-3 text-white" aria-hidden="true" />
                        ) : null}
                      </span>
                      <div
                        className={`min-w-0 flex-1 ${
                          renamingKeyframeName === entry.fullName ? "hidden" : ""
                        }`}
                      >
                        <Tooltip content={poseApplyTooltip} fullWidth>
                          <button
                            type="button"
                            disabled={poseApplyDisabled}
                            onClick={() => {
                              if (poseApplyDisabled) return;
                              fileActions.handleKeyframeSelect(
                                entry.fullName,
                                keyframeScopeWorldBodyUuid
                              );
                            }}
                            className="w-full min-w-0 truncate rounded-sm bg-transparent p-0 text-left text-inherit select-none focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 disabled:cursor-not-allowed disabled:opacity-45"
                          >
                            {entry.poseName}
                          </button>
                        </Tooltip>
                      </div>
                      {renamingKeyframeName === entry.fullName ? (
                        <input
                          type="text"
                          autoFocus={!isMobile}
                          value={renameDraft}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onBlur={() => commitRenamePose(entry)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitRenamePose(entry);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRenamePose();
                            }
                          }}
                          className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-900/90 px-2 py-0.5 text-sm text-gray-100 outline-none focus:border-gray-400"
                          aria-label={`Rename ${entry.poseName}`}
                        />
                      ) : null}
                      {renamingKeyframeName === entry.fullName ? (
                        <>
                          <Tooltip content="Save pose name">
                            <button
                              type="button"
                              aria-label={`Save ${entry.poseName} name`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                commitRenamePose(entry);
                              }}
                              className="shrink-0 rounded p-0.5 text-gray-300 transition-colors duration-150 hover:text-green-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                            >
                              <Check className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Cancel rename">
                            <button
                              type="button"
                              aria-label={`Cancel renaming ${entry.poseName}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelRenamePose();
                              }}
                              className="shrink-0 rounded p-0.5 text-gray-300 transition-colors duration-150 hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        </>
                      ) : !authoringLocked ? (
                        <>
                          <Tooltip content={`Rename ${entry.poseName}`}>
                            <button
                              type="button"
                              aria-label={`Rename ${entry.poseName}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                startRenamePose(entry);
                              }}
                              className="shrink-0 rounded p-0.5 text-gray-400 opacity-0 pointer-events-none transition-colors duration-150 hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 group-hover:opacity-100 group-hover:pointer-events-auto"
                            >
                              <Pencil className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        </>
                      ) : null}
                      {!authoringLocked ? (
                        <PanelDeleteIconButton
                          revealOnParentHover
                          className="shrink-0 p-0.5"
                          iconClassName="w-3 h-3"
                          ariaLabel={`Delete ${entry.poseName}`}
                          title={`Delete ${entry.poseName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            getHistoryActions().save(getSceneState());
                            if (entry.poseName === "neutral") {
                              markNeutralPoseDeleted(entry.fullName);
                            }
                            fileActions.deleteKeyframe(entry.fullName);
                          }}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default React.memo(ObjectPropertiesPanel);
